import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box, TextField, Typography, Paper, Divider, Stack,
  List, ListItem, ListItemIcon, ListItemText, Checkbox,
  Button, Tooltip, IconButton, Chip, Tab, Tabs,
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import AddIcon from '@mui/icons-material/Add';
import EventNoteIcon from '@mui/icons-material/EventNote';
import { DragDropContext, Droppable, Draggable, DropResult } from '@hello-pangea/dnd';
import { v4 as uuidv4 } from 'uuid';
import { useAppSelector, useAppDispatch } from '../../store/hooks';
import {
  addWorkstream,
  updateWorkstream,
  deleteWorkstream,
  DEFAULT_WORKSTREAM_STATES,
} from '../../store/slices/workstreamsSlice';
import {
  addWorkstreamSprint,
  reorderWorkstreamSprints,
  selectSprintsForWorkstream,
} from '../../store/slices/sprintsSlice';
import ModalActionFooter from '../../components/ModalActionFooter/ModalActionFooter';
import { formatDate, getSprintDateRange } from '../../utils/sprintDateUtils';

// ── Main component ───────────────────────────────────────────────────────────

const WorkstreamForm: React.FC = () => {
  const navigate = useNavigate();
  const { workstreamId } = useParams<{ workstreamId: string }>();
  const dispatch = useAppDispatch();
  const existingWorkstream = useAppSelector((state) =>
    state.workstreams.workstreams.find((w) => w.id === workstreamId)
  );
  const allWorkstreams = useAppSelector((state) => state.workstreams.workstreams);
  const pods = useAppSelector((state) => state.pods.pods);
  const sprintsConfig = useAppSelector((state) => state.sprints);
  const workstreamSprints = useAppSelector((state) =>
    workstreamId ? selectSprintsForWorkstream(state, workstreamId) : []
  );

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [selectedPodIds, setSelectedPodIds] = useState<string[]>([]);

  // Bottom-panel tab state
  const [activeTab, setActiveTab] = useState(0);

  useEffect(() => {
    if (existingWorkstream) {
      setName(existingWorkstream.name);
      setDescription(existingWorkstream.description ?? '');
      setSelectedPodIds(existingWorkstream.entries[0]?.podIds ?? []);
    }
  }, [existingWorkstream]);

  const getConflictingWorkstream = (podId: string) =>
    allWorkstreams.find(
      (w) => w.id !== workstreamId && (w.entries[0]?.podIds ?? []).includes(podId)
    ) ?? null;

  const handleTogglePod = (podId: string) => {
    setSelectedPodIds((prev) =>
      prev.includes(podId) ? prev.filter((id) => id !== podId) : [...prev, podId]
    );
  };

  const handleUnassignFromConflict = (podId: string, conflictingWorkstream: ReturnType<typeof getConflictingWorkstream>) => {
    if (!conflictingWorkstream) return;
    const existingPodIds = conflictingWorkstream.entries[0]?.podIds ?? [];
    const updatedPodIds = existingPodIds.filter((id) => id !== podId);
    dispatch(updateWorkstream({
      ...conflictingWorkstream,
      entries: updatedPodIds.length > 0 ? [{ sprintId: 'default', podIds: updatedPodIds }] : [],
      updatedAt: new Date().toISOString(),
    }));
  };

  const buildEntries = (podIds: string[]) => {
    if (podIds.length === 0) return [];
    return [{ sprintId: 'default', podIds }];
  };

  const handleSave = () => {
    if (!name.trim()) return;
    const now = new Date().toISOString();
    if (existingWorkstream) {
      dispatch(updateWorkstream({
        ...existingWorkstream,
        name: name.trim(),
        description: description.trim() || undefined,
        entries: buildEntries(selectedPodIds),
        updatedAt: now,
      }));
    } else {
      dispatch(addWorkstream({
        id: uuidv4(),
        name: name.trim(),
        description: description.trim() || undefined,
        entries: buildEntries(selectedPodIds),
        states: DEFAULT_WORKSTREAM_STATES,
        createdAt: now,
      }));
    }
    navigate('/epic-planner');
  };

  const handleDelete = () => {
    if (existingWorkstream) {
      dispatch(deleteWorkstream(existingWorkstream.id));
      navigate('/epic-planner');
    }
  };

  // ── Sprint helpers ────────────────────────────────────────────────────────

  const selectedPods = pods.filter((p) => selectedPodIds.includes(p.id));
  const defaultVelocity = selectedPods.reduce((sum, p) => sum + p.defaultVelocity, 0);

  const getOverrideVelocity = (sprintNumber: number): number | undefined => {
    const o = sprintsConfig.overrides.find(
      (o) => o.workstreamId === workstreamId && o.sprintIndex === sprintNumber - 1
    );
    return o?.velocity;
  };

  const handleAddSprint = () => {
    if (!workstreamId) return;
    const nextNumber = workstreamSprints.length > 0
      ? Math.max(...workstreamSprints.map((s) => s.number)) + 1
      : 1;
    const newSprint = {
      id: uuidv4(),
      workstreamId,
      number: nextNumber,
      storyIds: [],
    };
    dispatch(addWorkstreamSprint(newSprint));
    navigate(`/epic-planner/workstreams/${workstreamId}/sprints/${newSprint.id}/edit`);
  };

  const handleDragEnd = (result: DropResult) => {
    if (!result.destination || !workstreamId) return;
    const items = Array.from(workstreamSprints);
    const [removed] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, removed);
    dispatch(reorderWorkstreamSprints({ workstreamId, sprints: items }));
  };

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <Box sx={{ maxWidth: 1100, mx: 'auto' }}>
      {/* ── Top row: 2-col grid ── */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '2fr 1fr' },
          gap: 3,
          alignItems: 'start',
        }}
      >
        {/* ── Left Paper: workstream details ── */}
        <Paper elevation={2} sx={{ p: 3 }}>
          <Typography variant="h5" fontWeight={600} gutterBottom>
            {existingWorkstream ? 'Edit Workstream' : 'Create Workstream'}
          </Typography>
          <Divider sx={{ mb: 3 }} />
          <Stack spacing={3}>
            <TextField
              label="Workstream Name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              fullWidth
              required
              error={!name.trim()}
              helperText={!name.trim() ? 'Workstream name is required' : ''}
            />
            <TextField
              label="Description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              fullWidth
              multiline
              rows={3}
              placeholder="Optional description of this workstream's scope or purpose"
            />
          </Stack>
          <Box sx={{ mt: 4 }}>
            <ModalActionFooter
              onSave={handleSave}
              saveLabel={existingWorkstream ? 'Save' : 'Create'}
              saveDisabled={!name.trim()}
              onCancel={() => navigate('/epic-planner')}
              onDelete={existingWorkstream ? handleDelete : undefined}
              deleteLabel="Delete"
              deleteConfirmMessage="Are you sure you want to delete this workstream? This action cannot be undone."
            />
          </Box>
        </Paper>

        {/* ── Right Paper: Assigned Pods ── */}
        <Paper elevation={2} sx={{ p: 3, position: { md: 'sticky' }, top: { md: 80 } }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <Typography variant="subtitle1" color="text.secondary">
              Assigned Pods
            </Typography>
            <Tooltip
              title="Select one or more pods for this workstream. Each pod can only belong to one workstream at a time."
              arrow
              placement="right"
            >
              <IconButton size="small" aria-label="Pods information" sx={{ color: 'text.secondary' }}>
                <InfoOutlinedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
          {pods.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
              No pods have been created yet.
            </Typography>
          ) : (
            <Paper variant="outlined" sx={{ borderRadius: 1 }}>
              <List dense disablePadding>
                {pods.map((pod, index) => {
                  const isSelected = selectedPodIds.includes(pod.id);
                  const conflict = getConflictingWorkstream(pod.id);
                  const isDisabled = !!conflict;

                  return (
                    <React.Fragment key={pod.id}>
                      {index > 0 && <Divider component="li" />}
                      <ListItem
                        sx={{ py: 1.25, opacity: isDisabled ? 0.7 : 1 }}
                        secondaryAction={
                          conflict ? (
                            <Tooltip title={`Remove pod from "${conflict.name}" to make it available`}>
                              <Button
                                size="small"
                                color="warning"
                                onClick={() => handleUnassignFromConflict(pod.id, conflict)}
                              >
                                Unassign
                              </Button>
                            </Tooltip>
                          ) : undefined
                        }
                      >
                        <ListItemIcon sx={{ minWidth: 36 }}>
                          <Checkbox
                            edge="start"
                            checked={isSelected}
                            disabled={isDisabled}
                            onChange={() => handleTogglePod(pod.id)}
                            inputProps={{ 'aria-label': `Assign pod ${pod.name}` }}
                            size="small"
                          />
                        </ListItemIcon>
                        <ListItemText
                          primary={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                              <Typography variant="body2" fontWeight={isSelected ? 600 : 400}>
                                {pod.name}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {pod.defaultVelocity} pts/sprint
                              </Typography>
                            </Box>
                          }
                          secondary={
                            conflict ? (
                              <Typography variant="caption" color="warning.main">
                                Currently assigned to: {conflict.name}
                              </Typography>
                            ) : undefined
                          }
                        />
                      </ListItem>
                    </React.Fragment>
                  );
                })}
              </List>
            </Paper>
          )}
          {selectedPods.length > 0 && (
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
              {selectedPods.length} pod{selectedPods.length > 1 ? 's' : ''} selected — combined velocity: {defaultVelocity} pts/sprint
            </Typography>
          )}
        </Paper>
      </Box>

      {/* ── Bottom tabbed panel (only for existing workstreams) ── */}
      {existingWorkstream && (
        <Paper elevation={2} sx={{ mt: 3, overflow: 'hidden' }}>
          <Tabs
            value={activeTab}
            onChange={(_, v) => setActiveTab(v)}
            variant="fullWidth"
            sx={{ borderBottom: 1, borderColor: 'divider' }}
          >
            <Tab
              icon={<EventNoteIcon fontSize="small" />}
              aria-label="Sprint Management"
              title="Sprint Management"
              sx={{ minWidth: 0, py: 1.5 }}
            />
          </Tabs>

          {/* ── Tab 0: Sprint Management ── */}
          {activeTab === 0 && (
            <Box sx={{ p: 2 }}>
              {/* Sprint list — same style as EpicForm Story Breakdown */}
              <Box sx={{ gap: 1.5, mb: 1, backgroundColor: 'background.paper', p: 1, borderRadius: 1, borderColor: 'divider', borderWidth: 1, borderStyle: 'solid' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="subtitle1" color="text.secondary">Sprint Management</Typography>
                  <Button size="small" startIcon={<AddIcon />} onClick={handleAddSprint}>
                    Add Sprint
                  </Button>
                </Box>
                <Paper variant="outlined" sx={{ borderRadius: 1 }}>
                  {workstreamSprints.length === 0 && (
                    <ListItem>
                      <ListItemText
                        primary="No sprints yet. Click 'Add Sprint' to plan this workstream."
                        primaryTypographyProps={{ variant: 'body2', color: 'text.secondary' }}
                      />
                    </ListItem>
                  )}
                  <DragDropContext onDragEnd={handleDragEnd}>
                    <Droppable droppableId="workstream-sprints">
                      {(provided) => (
                        <div ref={provided.innerRef} {...provided.droppableProps}>
                          {workstreamSprints.map((sprint, idx) => {
                            const isLast = idx === workstreamSprints.length - 1;
                            const dateRange = getSprintDateRange(
                              sprintsConfig.startDate,
                              sprintsConfig.sprintLength,
                              sprint.number,
                            );
                            const overrideVelocity = getOverrideVelocity(sprint.number);
                            const displayVelocity = overrideVelocity ?? defaultVelocity;
                            const hasOverride = overrideVelocity !== undefined;

                            return (
                              <Draggable key={sprint.id} draggableId={sprint.id} index={idx}>
                                {(dragProvided, snapshot) => (
                                  <ListItem
                                    ref={dragProvided.innerRef}
                                    {...dragProvided.draggableProps}
                                    onClick={() => navigate(`/epic-planner/workstreams/${workstreamId}/sprints/${sprint.id}/edit`)}
                                    sx={{
                                      borderBottom: isLast ? 'none' : '1px solid',
                                      borderColor: 'divider',
                                      gap: 1,
                                      pr: 1,
                                      cursor: 'pointer',
                                      bgcolor: snapshot.isDragging ? 'action.selected' : 'transparent',
                                      '&:hover': { bgcolor: 'action.hover' },
                                    }}
                                  >
                                    <Box
                                      {...dragProvided.dragHandleProps}
                                      onKeyDown={(e) => e.stopPropagation()}
                                      onClick={(e) => e.stopPropagation()}
                                      sx={{ mr: 0.5, cursor: 'grab', color: 'text.secondary', display: 'flex', alignItems: 'center' }}
                                    >
                                      <DragIndicatorIcon fontSize="small" />
                                    </Box>
                                    <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                                      <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
                                        <Typography variant="body2" fontWeight={500}>
                                          Sprint {sprint.number}
                                          {sprint.nickname && (
                                            <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 0.5 }}>
                                              · {sprint.nickname}
                                            </Typography>
                                          )}
                                        </Typography>
                                        <Chip
                                          label={`${formatDate(dateRange.start)} – ${formatDate(dateRange.end)}`}
                                          size="small"
                                          variant="outlined"
                                          sx={{ fontSize: '0.65rem', height: 20 }}
                                        />
                                        <Chip
                                          label={`${displayVelocity} pts`}
                                          size="small"
                                          color={hasOverride ? 'warning' : 'default'}
                                          variant={hasOverride ? 'outlined' : 'filled'}
                                          sx={{ fontSize: '0.65rem', height: 20 }}
                                        />
                                        {sprint.storyIds.length > 0 && (
                                          <Chip
                                            label={`${sprint.storyIds.length} stor${sprint.storyIds.length === 1 ? 'y' : 'ies'}`}
                                            size="small"
                                            color="primary"
                                            variant="outlined"
                                            sx={{ fontSize: '0.65rem', height: 20 }}
                                          />
                                        )}
                                        {sprint.status && (
                                          <Chip
                                            label={sprint.status}
                                            size="small"
                                            color={sprint.status === 'Active' ? 'primary' : 'default'}
                                            variant="outlined"
                                            sx={{ fontSize: '0.65rem', height: 20 }}
                                          />
                                        )}
                                      </Box>
                                    </Box>
                                  </ListItem>
                                )}
                              </Draggable>
                            );
                          })}
                          {provided.placeholder}
                        </div>
                      )}
                    </Droppable>
                  </DragDropContext>
                </Paper>
              </Box>
            </Box>
          )}
        </Paper>
      )}
    </Box>
  );
};

export default WorkstreamForm;
